<template>
  <CommonLoading
		v-if="loading"
	/>
  <!-- Input de arquivo invisível para subir certificados customizados -->
  <input 
    type="file" 
    ref="fileInput" 
    accept=".pdf" 
    class="hidden" 
    @change="onFileSelected" 
  />
    <TransitionRoot as="template" :show="open">
      <Dialog class="relative z-50" @close="closeDialog">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>
  
        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-5xl sm:p-6">
                <!-- Header -->
                <div class="flex justify-between items-start">
                  <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">Editar Segurado</h2>
                  <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600 -mt-4">
                    <span class="sr-only">Fechar</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
  
                <!-- Form -->
                <div class="mt-4 space-y-6">
                  <!-- Campos principais -->
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div v-for="(field, index) in primaryFields" :key="index" class="flex flex-col">
                      <label :for="field" class="text-sm font-medium text-gray-900">
                        {{ formatFieldLabel(field) }}
                      </label>
                      <input
                        v-model="formData[field]"
                        :id="field"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        :type="field === 'data_nascimento' ? 'date' : 'text'"
                        :placeholder="'Digite ' + formatFieldLabel(field)"
                      />
                    </div>
                  </div>
                  <!-- Divider Badges -->
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>
                    <div class="relative flex items-center justify-between">
                      <span class="bg-white pr-3 text-base font-semibold text-gray-900">Marcações</span>
                      <button @click="badgeFormOpen = true" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                        <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                        <span>Adicionar</span>
                      </button>
                    </div>
                  </div>
                  <!-- Badges -->
                  <div class="flex justify-evenly">
                    <BadgesView
                      :badges="formData.badges"
                      @delete-badge= "removeBadge"
                    />
                  </div>
                  
                  <!-- Telefones -->
                  <div>
                    <!-- Divider Telefone -->
                    <div class="relative mb-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex items-center justify-between">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Telefones</span>
                        <button @click="addItem('telefones')" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                          <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                          <span>Adicionar</span>
                        </button>
                      </div>
                    </div>
                    <div class="space-y-4">
                      <div v-for="(telefone, index) in formData.telefones || []" :key="index" class="flex items-center gap-4">
                        <input
                          v-model="formData.telefones[index].numero"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Número do telefone"
                          @input="validateTelefone(index)"
                        />
                        <button
                          @click="handleRemove('telefone', index, telefone)"
                          class="text-red-500 hover:text-red-700"
                        >
                          Remover
                        </button>
                      </div>

                      <button v-if="formData.telefones?.length > 0" 
                        @click="handleBulkSave('telefones', formData.telefones)"
                        type="button" 
                        class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500">
                          Salvar alterações telefone
                        </button>
                    </div>

                  </div>
                  <!-- Empresas -->
                  <div v-if="!formData.socio || formData.socio === 'segurado'">
                    <input
                      v-model="empresaBusca"
                      @input="buscarEmpresas"
                      type="text"
                      placeholder="Digite para buscar empresas..."
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                    <ul v-if="sugestoesEmpresas.length" class="mt-2 border rounded p-2 max-h-40 overflow-y-auto">
                      <li
                        v-for="(empresa, index) in sugestoesEmpresas"
                        :key="index"
                        @click="confirmarVinculo(empresa)"
                        class="cursor-pointer hover:bg-gray-100 p-1 rounded"
                      >
                        {{ empresa.nome }}
                      </li>
                    </ul>
                    <!-- Divider Empresas -->
                    <div class="relative m-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex justify-start">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Empresas</span>
                      </div>
                    </div>
                  </div>

                   <!-- Emails -->
                   <div>
                    <div class="space-y-4">
                      <div v-for="(email, index) in formData.emails" :key="index" class="flex items-center gap-4">
                        <input
                          v-model="formData.emails[index].email"
                          :id="'email-' + index"
                          class="block w-full rounded-md border-gray-300 sm:text-sm"
                          type="email"
                          placeholder="Email"
                          @input="validateEmail(index)"
                        />
                        <button
                          @click="handleRemove('email', index, email)"
                          class="text-red-600 hover:text-red-800"
                        >
                          Remover
                        </button>
                      </div>
                      <button
                        v-if="formData.emails?.length > 0"
                        @click="handleBulkSave('emails', formData.emails)"
                        type="button"
                        class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500"
                      >
                        Salvar alterações Emails
                      </button>

                      <!-- Divider Emails -->
                      <div class="relative mb-5">
                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                          <div class="w-full border-t border-gray-300" />
                        </div>
                        <div class="relative flex items-center justify-between">
                          <span class="bg-white pr-3 text-base font-semibold text-gray-900">Emails</span>
                          <button @click="addItem('emails')" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                            <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                            <span>Adicionar</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p v-if="formData.socio === 'dependente'" class="text-sm text-gray-700 mb-4">
                    Dependente de: <strong>{{ formData.segurado?.nome || '---' }}</strong>
                  </p>
                  <!-- Dependentes -->
                  <div v-if="!formData.socio || formData.socio === 'segurado'">
                    <div class="space-y-4">
                      <div v-for="(dependente, index) in formData.dependentes" :key="index" class="grid grid-cols-2 gap-4">
                        <input
                          v-model="formData.dependentes[index].cpf"
                          :id="'dependente-cpf-' + index"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="CPF"
                          @input="validateDependente(index)"
                        />
                        <input
                          v-model="formData.dependentes[index].nome"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Nome"
                        />
                        <input
                          v-model="formData.dependentes[index].data_nascimento"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="date"
                          placeholder="Data de Nascimento"
                        />
                        <input
                          v-model="formData.dependentes[index].email"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="email"
                          placeholder="Email"
                        />
                        <button
                          @click="handleRemove('dependente', index, dependente)"
                          class="text-red-500 hover:text-red-700"
                        >
                          Remover
                        </button>
                      </div>
                      <button
                        v-if="formData.dependentes?.length > 0"
                       @click="handleBulkSave('dependentes', formData.dependentes)"
                        type="button"
                        class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500"
                      >
                        Salvar alterações Dependentes
                      </button>
                    </div>
                    <!-- Divider Dependentes -->
                    <div class="relative mt-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex items-center justify-between">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Dependentes</span>
                        <button @click="addItem('dependentes')" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                          <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                          <span>Adicionar</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Conjuges -->
                  <div v-if="formData.conjuges?.length > 0">
                    <div class="space-y-4">
                      <div v-for="(conjuge, index) in formData.conjuges" :key="index" class="grid grid-cols-2 gap-4">
                        <input
                          v-model="formData.conjuges[index].cpf"
                          :id="'conjuge-cpf-' + index"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="CPF"
                          @input="validateConjuge(index)"
                        />
                        <input
                          v-model="formData.conjuges[index].nome"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Nome"
                        />
                        <input
                          v-model="formData.conjuges[index].data_nascimento"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="date"
                          placeholder="Data de Nascimento"
                        />
                        <button
                         @click="handleRemove('conjuge', index, conjuge)"
                          class="text-red-600 hover:text-red-800"
                        >
                          Remover
                        </button>
                      </div>
                      <button
                        v-if="formData.conjuges.length > 0"
                        @click="handleBulkSave('conjuges', formData.conjuges)"
                        type="button"
                        class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500"
                      >
                        Salvar alterações Cônjuges
                      </button>
                    </div>
                    <!-- Divider Conjuge -->
                    <div class="relative mt-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex items-center justify-between">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Conjuge</span>
                        <button @click="addItem('conjuge')" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                          <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                          <span>Adicionar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Endereços -->
                  <div>
                    <div class="space-y-4">
                      <div v-for="(endereco, index) in formData.enderecos" :key="index" class="grid grid-cols-2 gap-4">
                        <input
                          v-model="formData.enderecos[index].logradouro"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Logradouro"
                        />
                        <input
                          v-model="formData.enderecos[index].cidade"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Cidade"
                        />
                        <input
                          v-model="formData.enderecos[index].bairro"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Bairro"
                        />
                        <input
                          v-model="formData.enderecos[index].cep"
                          :id="'endereco-cep-' + index"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="CEP"
                          @input="validateEndereco(index)"
                        />
                        <input
                          v-model="formData.enderecos[index].estado"
                          maxlength="2"
                          @input="validateInput(index)"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Estado"
                        />
                        <input
                          v-model="formData.enderecos[index].numero"
                          :id="'endereco-numero-' + index"
                          class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                          type="text"
                          placeholder="Número"
                          @input="validateEndereco(index)"
                        />
                        <button
                          @click="handleRemove('endereco', index, endereco)"
                          class="text-red-600 hover:text-red-800"
                        >
                          Remover
                        </button>
                      </div>
                      <button
                        v-if="formData.enderecos?.length > 0"
                        @click="handleBulkSave('enderecos', formData.enderecos)"
                        type="button"
                        class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500"
                      >
                        Salvar alterações Endereços
                      </button>
                    </div>
                    <!-- Divider Endereços -->
                    <div class="relative mt-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex items-center justify-between">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Endereços</span>
                        <button @click="addItem('enderecos')" type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                          <PlusIcon class="-ml-1 -mr-0.5 size-5 text-gray-400" aria-hidden="true" />
                          <span>Adicionar</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Apólices -->
                  <div>
                    <!-- Divider Apólices -->
                    <div class="relative mb-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex items-center justify-between">
                        <span class="bg-white pr-3 text-base font-semibold text-gray-900">Apólices Vincualdas</span>
                      </div>
                    </div>

                    <div v-if="loading" class="flex justify-center py-4">
                      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>

                    <div v-else-if="!formData.apolices || formData.apolices.length === 0" class="text-center py-4 text-sm text-gray-500 italic">
                      Nenhuma apólice vinculada encontrada.
                    </div>

                    <div v-else class="w-full overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                      <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-50">
                          <tr>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Apólice</th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plano</th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th scope="col" class="relative px-3 py-3"><span class="sr-only">Ações</span></th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr v-for="vinculo in formData.apolices" :key="vinculo.id" class="hover:bg-gray-50 transition-colors">
                            <td class="px-3 py-4 text-sm text-gray-900 max-w-[200px] break-words">
                              <div class="font-medium">{{ vinculo.apolice?.numero }}</div>
                              <div class="text-xs text-gray-500">{{ vinculo.apolice?.seguradora?.nome }}</div>
                            </td>
                            <td class="px-3 py-4 text-sm text-gray-500 max-w-[280px] break-words">
                              {{ vinculo.apolice?.empresa?.nome }}
                            </td>
                            <td class="px-3 py-4 text-sm text-gray-500 max-w-[200px] break-words">
                              {{ vinculo.plano?.nome }}
                            </td>
                            <td class="px-3 py-4 whitespace-nowrap">
                              <span :class="getStatusBadgeClass(vinculo.status_vinculo)" class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                                {{ vinculo.status_vinculo }}
                              </span>
                              <div v-if="vinculo.data_cancelamento" class="text-[10px] text-gray-400 mt-0.5">
                                Canc. em: {{ formatDate(vinculo.data_cancelamento) }}
                              </div>
                            </td>
                            <td class="px-3 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                              <span v-if="vinculo.status_vinculo !== 'cancelado'" class="space-x-3">
                                <!-- Baixar/Visualizar PDF Personalizado se existir, senão Gerar PDF Dinâmico -->
                                <button 
                                  v-if="vinculo.custom_certificate_url"
                                  @click="abrirCertificadoCustomizado(vinculo)"
                                  class="text-emerald-600 hover:text-emerald-800 font-semibold"
                                  title="Baixar Certificado Personalizado"
                                >
                                  Baixar
                                </button>
                                <button 
                                  v-else
                                  @click="gerarCertificado(vinculo)"
                                  class="text-indigo-600 hover:text-indigo-900 font-semibold"
                                  title="Gerar Certificado Dinâmico"
                                >
                                  Certificado
                                </button>
                                
                                <!-- Subir / Substituir PDF Personalizado -->
                                <button 
                                  @click="triggerUpload(vinculo)"
                                  class="text-blue-600 hover:text-blue-900 font-semibold"
                                  :title="vinculo.custom_certificate_url ? 'Substituir Certificado Personalizado' : 'Subir Certificado Personalizado'"
                                >
                                  {{ vinculo.custom_certificate_url ? 'Substituir' : 'Subir PDF' }}
                                </button>

                                <!-- Excluir PDF Personalizado -->
                                <button 
                                  v-if="vinculo.custom_certificate_url"
                                  @click="excluirCertificadoCustomizado(vinculo)"
                                  class="text-red-400 hover:text-red-600 font-semibold"
                                  title="Excluir Certificado Personalizado"
                                >
                                  Excluir
                                </button>
                              </span>

                              <!-- Cancelar Vínculo -->
                              <button 
                                v-if="vinculo.status_vinculo !== 'cancelado'"
                                @click="handleCancelarVinculo(vinculo)"
                                class="text-red-600 hover:text-red-900 font-semibold"
                                title="Cancelar Vínculo"
                              >
                                Cancelar
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Agendamentos --> 
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div
                      v-for="(agendamento, index) in formData.lista_de_agendamentos"
                      :key="index"
                      class="bg-white rounded-lg shadow p-4"
                    >
                      <h3 class="text-lg font-semibold text-gray-800">Agendamento</h3>
                      <div class="mt-2 space-y-2">
                        <div class="w-full">
                          <span class="font-medium text-gray-700">Agendador:</span>
                          <div class="truncate w-full text-gray-600">{{ agendamento.agendador_email }}</div>
                        </div>
                        <div v-if="agendamento.consultor_email" class="w-full">
                          <span class="font-medium text-gray-700">Consultor:</span>
                          <div class="truncate w-full text-gray-600">{{ agendamento.consultor_email }}</div>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Data do Agendamento:</span>
                          <span class="text-gray-600">{{ formatDate(agendamento.data_agendamento) }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Data de Retorno:</span>
                          <span class="text-gray-600">{{ formatDate(agendamento.data_retorno) }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Data da Visita:</span>
                          <span class="text-gray-600">{{ formatDate(agendamento.data_visita) }}</span>
                        </div>
                        <div>
                          <span
                            class="inline-flex items-center justify-center gap-x-1.5 rounded-full px-1.5 py-0.5 text-xs font-medium"
                            :class="{
                              'bg-yellow-100 text-yellow-800': agendamento.status === 'pendente',
                              'bg-green-100 text-green-700': agendamento.status === 'concluido',
                              'bg-red-100 text-red-700': agendamento.status === 'cancelado'
                            }"
                          >
                            <svg
                              class="h-2 w-2"
                              viewBox="0 0 6 6"
                              :class="{
                                'fill-yellow-500': agendamento.status === 'pendente',
                                'fill-green-500': agendamento.status === 'concluido',
                                'fill-red-500': agendamento.status === 'cancelado'
                              }"
                              aria-hidden="true"
                            >
                              <circle cx="3" cy="3" r="3" />
                            </svg>
                            {{ agendamento.status }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Empresas --> 
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div
                      v-for="(empresa, index) in formData.empresas"
                      :key="index"
                      class="bg-white rounded-lg shadow p-4"
                    >
                      <h3 class="text-lg font-semibold text-gray-800">Empresa</h3>
                      <div class="mt-2 space-y-2">
                        <div>
                          <span class="font-medium text-gray-700">Nome:</span>
                          <span class="text-gray-600">{{ empresa.nome }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">CNPJ:</span>
                          <span class="text-gray-600">{{ empresa.cnpj }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Endereço:</span>
                          <span class="text-gray-600">{{ empresa.endereco }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Telefone:</span>
                          <span class="text-gray-600">{{ empresa.telefone }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-700">Email:</span>
                          <span class="text-gray-600">{{ empresa.email }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Agendamento Avulsos --> 
                  <div v-if="podeCriarRotaAvulsa" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div class="mb-4">
                      <h3 class="text-lg font-semibold text-gray-800">
                        {{ isConsultor ? 'Minha Rota Avulsa' : 'Rota Avulsa' }}
                      </h3>

                      <!-- Se for Consultor: Auto-atribuição direta sem dropdowns -->
                      <div v-if="isConsultor" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p class="text-xs font-bold text-blue-800 uppercase tracking-wider">🎯 Atribuição Direta</p>
                        <p class="text-sm font-semibold text-blue-900 mt-1">A visita será criada diretamente na sua própria agenda.</p>
                        <p class="text-xs text-gray-600 mt-0.5">{{ onlineUser?.user?.name || onlineUser?.user?.email }}</p>
                      </div>

                      <!-- Se for Gestor/Roteirização/Agendador: Escolha manual -->
                      <div v-else>
                        <label for="agendadores" class="block text-sm font-medium text-gray-900 mt-2">Agendadores</label>
                        <select 
                          v-model="rotaAvulsaForm.agendador_email"
                          id="agendadores" 
                          name="agendadores" 
                          class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                        >
                          <option value="">Selecione um Agendador</option>
                          <option v-for="agendador in agendadoresData" :key="agendador.email" :value="agendador.email">
                            {{ agendador.name }}
                          </option>
                        </select>

                        <label for="consultores" class="block text-sm font-medium text-gray-900 mt-4">Consultor (Opcional)</label>
                        <select 
                          v-model="rotaAvulsaForm.agendador_email"
                          id="consultores" 
                          name="consultores" 
                          class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                        >
                          <option value="">Selecione um Consultor (Opcional)</option>
                          <option v-for="consultor in consultoresData" :key="consultor.email" :value="consultor.email">
                            {{ consultor.name }}
                          </option>
                        </select>

                        <!-- Exibição do selecionado -->
                        <p v-if="selectedUserText" class="mt-4 text-xs font-semibold text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-100">
                          Selecionado: {{ selectedUserText.tipo }} — {{ selectedUserText.nome }}
                        </p>
                      </div>

                      <input
                        v-model="rotaAvulsaForm.nome"
                        class="mt-4 block w-full rounded-md border border-gray-300 shadow-sm text-sm"
                        type="text"
                        :placeholder="isConsultor ? 'Nome da rota (opcional)' : 'Nome da rota'"
                      />

                      <!-- Botão de Criação do Agendamento -->
                      <div class="w-full mt-6">
                        <button @click="sendAvulsaForm" type="button" class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500">
                          {{ isConsultor ? 'Criar Minha Rota Avulsa' : 'Criar Rota Avulsa' }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Linha Temporal -->
                  <div class="w-full mt-6">
                    <div class="relative mb-5">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-300" />
                      </div>
                      <div class="relative flex justify-center">
                        <span class="bg-white px-2 font-semibold text-blue-700">Linha Temporal do Associado</span>
                      </div>
                    </div>
                    <div class="max-h-[400px] overflow-y-auto px-4 w-full mt-6">
                      <ol class="relative border-s border-gray-200 dark:border-gray-700">
                        <li
                          v-for="item in formData.timeline"
                          :key="item.id"
                          class="mb-10 ms-4"
                        >
                          <div class="absolute w-3 h-3 bg-gray-200 rounded-full mt-1.5 -start-1.5 border border-white dark:border-gray-900 dark:bg-gray-700"></div>
                          <time class="mb-1 text-sm font-normal leading-none text-gray-400 dark:text-gray-500">
                            {{ new Date(item.created_at).toLocaleString() }}
                          </time>
                          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                            {{ item.titulo || translateTimelineType(item.tipo) }}
                          </h3>
                          <p class="mb-1 text-sm text-gray-500 dark:text-gray-400 italic">
                            Autor: {{ item.autor_email }}
                          </p>
                          <p class="text-base font-normal text-gray-700 dark:text-gray-300">
                            {{ item.descricao }}
                          </p>
                          <!-- Dados extras formatados -->
                          <div v-if="item.dados_novos || item.dados_anteriores" class="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 border border-gray-100">
                             <div v-if="item.dados_anteriores">
                                <strong>Anterior:</strong> {{ formatTimelineData(item.dados_anteriores) }}
                             </div>
                             <div v-if="item.dados_novos">
                                <strong>Novo:</strong> {{ formatTimelineData(item.dados_novos) }}
                             </div>
                          </div>
                        </li>
                      </ol>
                    </div>
                  </div>

                  <!-- Botão de salvar -->
                  <div class="w-full">
                    <button @click="saveData" class="flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700">Salvar</button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
        <div @click.stop>
          <BadgesForm
            :openBadge="badgeFormOpen"
            @update-open-badge="badgeFormOpen = false"
            @data-badge= "adicionarMarcacao"
          />
        </div>
      </Dialog>
    </TransitionRoot>
  </template>
<script>

import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Swal from "sweetalert2";
import debounce from 'lodash/debounce';
import { gerarCertificadoAPMP, gerarCertificadoMapfreGeral, gerarCertificadoPadrao } from "./certificadoTemplates.js";
import EmpresasRepository from '@/services/EmpresasRepository.vue';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';
import CommonLoading from '@/components/generalcomponents/CommonLoading.vue';
import SeguradosRepository from '@/services/SeguradosRepository.vue';
import BadgesView from '@/components/generalcomponents/BadgesView.vue';
import { PlusIcon } from '@heroicons/vue/20/solid'
import BadgesForm from '@/components/generalcomponents/BadgesForm.vue';

export default {
  name: 'EditSegurado',
  emits: [
    'update-open', 
    'save', 
    'avulso', 
    'seguradoEmpresa', 
    'save-bulk',
    'remove-item',
    'segurado-badge',
    'remove-badge'
    ],
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    segurado: {
      type: Object,
      required: true,
    },
    agendadores: {
      type: Array,
      required: true,
    },
    consultores: {
      type: Array,
      required: true,
    },
  },
  created() {
    this.buscarEmpresas = debounce((...args) => {
      this._buscarEmpresas(...args);
    }, 400); // 400ms de espera
  },
  data() {
    return {
      rotaAvulsaForm: {
        agendador_email:'',
        consultor_email:'',
        nome:'',
        tipo:this.segurado.socio,
        id:this.segurado.id
      },
      formData: {}, // Inicializa vazio
      primaryFields: ['cpf', 'nome', 'data_nascimento', 'email', 'telefone', 'carreira'], // Campos principais
      agendadoresData: this.agendadores,
      consultoresData: this.consultores,
      empresaBusca: '',
      sugestoesEmpresas: [],
      onlineUser: JSON.parse(window.localStorage.getItem("myUser")),
      loading: false,
      badgeFormOpen: false,
      selectedVinculo: null
    };
  },
  computed: {
    isConsultor() {
      const grupos = this.onlineUser?.user?.groups || [];
      return grupos.includes('consultores') && !grupos.includes('admin') && !grupos.includes('master.justech') && !grupos.includes('coord.consultoressna');
    },
    podeCriarRotaAvulsa() {
      const grupos = this.onlineUser?.user?.groups || [];
      const gruposPermitidos = ['roteirizacao', 'coord.consultoressna', 'master', 'master.justech', 'admin', 'consultores'];
      return grupos.some(g => gruposPermitidos.includes(g));
    },
    selectedUserText() {
      const email = this.rotaAvulsaForm.agendador_email;
      if (!email) return null;

      const agendador = this.agendadoresData.find(a => a.email === email);
      if (agendador) {
        return { tipo: 'Agendador', nome: agendador.name };
      }

      const consultor = this.consultoresData.find(c => c.email === email);
      if (consultor) {
        return { tipo: 'Consultor', nome: consultor.name };
      }

      return null;
    }
  },
  watch: {
    segurado: {
      immediate: true,
      deep: true, // Observa mudanças dentro do objeto também
      handler(newSegurado) {
        if (newSegurado) {
          this.formData = JSON.parse(JSON.stringify(newSegurado)); // Garante cópia independente
          this.rotaAvulsaForm = {
            agendador_email: '',
            consultor_email: '',
            nome: '',
            tipo: newSegurado.socio,
            id: newSegurado.id,
          };
        }
      },
    },
    agendadores: {
      immediate: true,
      deep: true,
      handler(newAgendadores) {
        if (newAgendadores) {
          this.agendadoresData = newAgendadores;
        }
      },
    },
    consultores: {
      immediate: true,
      deep: true,
      handler(newConsultores) {
        if (newConsultores) {
          this.consultoresData = newConsultores;
        }
      },
    },
  },
  mounted() {
  },
  methods: {
    closeDialog() {
      this.$emit('update-open', false);
    },
    async handleCancelarVinculo(vinculo) {
      const { value: dCancelamento } = await Swal.fire({
        title: 'Cancelar Vínculo',
        text: `Deseja cancelar o vínculo com a apólice ${vinculo.apolice?.numero}? IMPORTANTE: Se já houver uma prévia aberta ou vigência fechada para este mês, o cancelamento será processado apenas no próximo mês.`,
        input: 'date',
        inputLabel: 'Data do Cancelamento',
        inputValue: new Date().toISOString().split('T')[0],
        showCancelButton: true,
        confirmButtonText: 'Confirmar Cancelamento',
        cancelButtonText: 'Sair',
        confirmButtonColor: '#d33',
        inputValidator: (value) => {
          if (!value) {
            return 'Você precisa selecionar uma data!'
          }
        }
      });

      if (dCancelamento) {
        this.loading = true;
        const repo = new SeguradosRepository();
        try {
          const response = await repo.cancelarVinculoApolice(this.onlineUser.token, vinculo.id, dCancelamento);
          if (response?.status === 200) {
            this.mostrarAlerta();
            // Emitir evento para o pai atualizar o segurado completo
            this.$emit('seguradoEmpresa', this.formData.id);
          }
        } catch (error) {
          this.mostrarAlertaErro(error?.response?.data?.message || 'Erro ao cancelar vínculo');
        } finally {
          this.loading = false;
        }
      }
    },
    abrirCertificadoCustomizado(vinculo) {
      if (vinculo.custom_certificate_url) {
        window.open(vinculo.custom_certificate_url, '_blank');
      }
    },
    triggerUpload(vinculo) {
      this.selectedVinculo = vinculo;
      this.$refs.fileInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validar extensão PDF
      if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
        this.mostrarAlertaErro('O arquivo selecionado deve ser do formato PDF.');
        event.target.value = '';
        return;
      }

      // Validar tamanho máximo (15MB)
      if (file.size > 15 * 1024 * 1024) {
        this.mostrarAlertaErro('O tamanho do arquivo não pode ser maior que 15MB.');
        event.target.value = '';
        return;
      }

      this.loading = true;
      const repo = new SeguradosRepository();
      try {
        const response = await repo.uploadCertificate(this.onlineUser.token, this.selectedVinculo.id, file);
        if (response?.status === 200 || response?.data?.status) {
          this.mostrarAlerta();
          this.$emit('seguradoEmpresa', this.formData.id);
          this.$emit('segurado-empresa', this.formData.id);
        }
      } catch (error) {
        console.error("Erro ao subir o certificado:", error);
        this.mostrarAlertaErro(error?.response?.data?.message || 'Erro ao realizar upload do certificado.');
      } finally {
        event.target.value = '';
        this.loading = false;
      }
    },
    async excluirCertificadoCustomizado(vinculo) {
      const result = await Swal.fire({
        title: 'Excluir Certificado?',
        text: `Deseja realmente excluir o certificado personalizado da apólice ${vinculo.apolice?.numero}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
      });

      if (result.isConfirmed) {
        this.loading = true;
        const repo = new SeguradosRepository();
        try {
          const response = await repo.deleteCertificate(this.onlineUser.token, vinculo.id);
          if (response?.status === 200 || response?.data?.status) {
            this.mostrarAlerta();
            this.$emit('seguradoEmpresa', this.formData.id);
            this.$emit('segurado-empresa', this.formData.id);
          }
        } catch (error) {
          console.error("Erro ao excluir o certificado:", error);
          this.mostrarAlertaErro(error?.response?.data?.message || 'Erro ao excluir o certificado.');
        } finally {
          this.loading = false;
        }
      }
    },
    async gerarCertificado(vinculo) {
      this.loading = true;
      const repo = new SeguradosRepository();
      try {
        const response = await repo.getCertificateData(this.onlineUser.token, vinculo.id);
        if (response?.status === 200 && response.data?.status) {
          const resData = response.data.data;
          
          const isMapfre = resData.seguradora.nome.toUpperCase().includes('MAPFRE');

          const cnpjLimpo = resData.estipulante.cnpj ? resData.estipulante.cnpj.replace(/\D/g, '') : '';
          const cnpjsAPMP = ['09193343000194', '05811435000130', '61278818002613'];
          const isAPMP = cnpjsAPMP.includes(cnpjLimpo);

          if (isMapfre) {
            if (isAPMP) {
              gerarCertificadoAPMP(resData);
            } else {
              gerarCertificadoMapfreGeral(resData);
            }
          } else {
            gerarCertificadoPadrao(resData);
          }
        } else {
          throw new Error(response.data?.message || 'Falha ao processar dados no servidor.');
        }
      } catch (error) {
        console.error("Erro ao gerar certificado:", error);
        this.mostrarAlertaErro(error?.response?.data?.message || error?.message || 'Erro interno ao gerar o PDF do certificado.');
      } finally {
        this.loading = false;
      }
    },
    getStatusBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'ativo') return 'bg-green-100 text-green-800';
      if (s === 'cancelado') return 'bg-red-100 text-red-800';
      if (s === 'inclusao_pendente') return 'bg-amber-100 text-amber-800';
      return 'bg-gray-100 text-gray-800';
    },
    saveData() {
      this.cleanCpf(); // Limpa a formatação do CPF
      const payload = {
        id: this.formData.id,
        nome: this.formData.nome,
        cpf: this.formData.cpf,
        data_nascimento: this.formData.data_nascimento,
        socio: this.formData.socio,
        carreira: this.formData.carreira,
        email: this.formData.email,
        telefone: (this.formData.telefone || '').replace(/\D/g, '')
      };

      this.$emit('save', payload);
    },
    sendAvulsaForm() {
      if (this.isConsultor) {
        const userEmail = this.onlineUser?.user?.email || this.onlineUser?.email;
        this.rotaAvulsaForm.agendador_email = userEmail;
        this.rotaAvulsaForm.consultor_email = userEmail;
        if (!this.rotaAvulsaForm.nome) {
          this.rotaAvulsaForm.nome = 'Rota Avulsa - ' + (this.formData.nome || 'Segurado');
        }
      } else {
        if (!this.rotaAvulsaForm.agendador_email || !this.rotaAvulsaForm.nome) {
          this.mostrarAlertaErro('Por favor, preencha os campos Nome e Agendador.');
          return;
        }
      }
      
      this.$emit('avulso', this.rotaAvulsaForm);
    },

    handleBulkSave(tipo, lista) {
      const normalizers = {
        telefones: tel => ({
          ...tel,
          numero: tel.numero.trim().replace(/\D/g, ''),
        }),
        emails: email => ({
          ...email,
          email: email.email.trim().toLowerCase()
        }),
        enderecos: end => ({
          ...end,
          cep: (end.cep || '').trim().replace(/\D/g, ''),
          numero: (end.numero || '').toString().trim(),
          estado: (end.estado || '').trim().toUpperCase(),
        }),
        dependentes: dep => ({
          ...dep,
          cpf: dep.cpf.trim().replace(/\D/g, ''),
          email: dep.email ? dep.email.trim().toLowerCase() : '',
        }),
        conjuges: conj => ({
          ...conj,
          cpf: conj.cpf.trim().replace(/\D/g, ''),
        }),
      };

      const normalizer = normalizers[tipo];
      if (!normalizer || !Array.isArray(lista)) return;

      const normalized = lista.map(normalizer);

      const duplicados = new Set();
      const chavesUnicas = {
        telefones: t => t.numero,
        emails: e => e.email,
        enderecos: e => `${e.cep}-${e.numero}`,
        dependentes: d => d.cpf,
        conjuges: c => c.cpf,
      };
      const chave = chavesUnicas[tipo];

      for (let i = 0; i < normalized.length; i++) {
        const v = chave(normalized[i]);
        if (v && duplicados.has(v)) {
          this.mostrarAlertaErro(`Existem duplicatas em ${tipo}.`);
          return;
        }
        duplicados.add(v);
      }

      const hasEmpty = normalized.some(obj => Object.values(obj).some(v => v === ''));
      if (hasEmpty) {
        this.mostrarAlertaErro(`Existem campos vazios em ${tipo}.`);
        return;
      }

      // ⬅️ Emitir com prefixo, ex: "save-segurado.telefones"
      this.$emit('save-bulk', {normalized,tipo});
    },

    cleanCpf() {
      if (this.formData.cpf) {
        // Remove pontos e hífens do CPF
        this.formData.cpf = this.formData.cpf.replace(/\D/g, '');
      }
    },
    validateTelefone(index) {
      const currentNumber = this.formData.telefones[index].numero.trim();
      if (currentNumber === '') return; // Ignorar se estiver vazio
      this.formData.telefones[index].segurado_id = this.formData.id;
      // Normalizar o número (remover caracteres não numéricos, se necessário)
      const normalizedNumber = currentNumber.replace(/\D/g, '');

      // Verificar se o número já existe em outro índice
      const isDuplicate = this.formData.telefones.some((telefone, i) => {
        if (i !== index) {
          const otherNumber = telefone.numero.trim().replace(/\D/g, '');
          return otherNumber === normalizedNumber && otherNumber !== '';
        }
        return false;
      });

      if (isDuplicate) {
        this.mostrarAlertaErro('Este número de telefone já existe na lista.');
        // Limpar o campo duplicado
        this.formData.telefones[index].numero = '';
      }
    },

    validateEndereco(index) {
      const currentCep = (this.formData.enderecos[index].cep || '').trim();
      let currentNumero = this.formData.enderecos[index].numero;

      this.formData.enderecos[index].segurado_id = this.formData.id;

      const normalizedCep = currentCep.replace(/\D/g, '');
      const normalizedNumero = currentNumero ? currentNumero.toString().trim().toLowerCase() : '';

      const isDuplicate = this.formData.enderecos.some((end, i) => {
        if (i !== index) {
          const otherCep = (end.cep || '').trim().replace(/\D/g, '');
          const otherNumero = end.numero ? end.numero.toString().trim().toLowerCase() : '';
          return otherCep === normalizedCep && otherNumero === normalizedNumero && otherCep !== '';
        }
        return false;
      });

      if (isDuplicate) {
        this.mostrarAlertaErro('Este endereço (CEP e número) já existe na lista.');
        this.formData.enderecos[index].cep = '';
        this.formData.enderecos[index].numero = '';
      }
    },

    validateEmail(index) {
      const currentEmail = this.formData.emails[index].email.trim().toLowerCase();
      if (currentEmail === '') return;

      const isDuplicate = this.formData.emails.some((email, i) => {
        if (i !== index) {
          const otherEmail = email.email.trim().toLowerCase();
          return otherEmail === currentEmail && otherEmail !== '';
        }
        return false;
      });

      if (isDuplicate) {
        this.mostrarAlertaErro('Este email já existe na lista.');
        this.formData.emails[index].email = '';
      }
    },

    validateDependente(index) {
      const currentCpf = this.formData.dependentes[index].cpf.trim();
      if (currentCpf === '') return;
        this.formData.dependentes[index].segurado_id = this.formData.id;
        const normalizedCpf = currentCpf.replace(/\D/g, '');
        const isDuplicate = this.formData.dependentes.some((dep, i) => {
        if (i !== index) {
          const otherCpf = dep.cpf.trim().replace(/\D/g, '');
          return otherCpf === normalizedCpf && otherCpf !== '';
        }
        return false;
      });

      if (isDuplicate) {
        this.mostrarAlertaErro('Este CPF já existe na lista de dependentes.');
        this.formData.dependentes[index].cpf = '';
      }
    },

    validateConjuge(index) {
      const currentCpf = this.formData.conjuges[index].cpf.trim();
      if (currentCpf === '') return;

      const normalizedCpf = currentCpf.replace(/\D/g, '');
      const isDuplicate = this.formData.conjuges.some((conj, i) => {
        if (i !== index) {
          const otherCpf = conj.cpf.trim().replace(/\D/g, '');
          return otherCpf === normalizedCpf && otherCpf !== '';
        }
        return false;
      });

      if (isDuplicate) {
        this.mostrarAlertaErro('Este CPF já existe na lista de cônjuges.');
        this.formData.conjuges[index].cpf = '';
      }
    },
    
    formatTimelineData(data) {
      if (!data) return '';
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        if (typeof parsed !== 'object' || parsed === null) return parsed;
        
        return Object.entries(parsed)
          .map(([key, value]) => {
            if (value === null || value === undefined || value === '') return null;
            
            const blacklist = ['id', 'created_at', 'updated_at', 'segurado_id', 'dependente_id'];
            if (blacklist.includes(key)) return null;

            const label = this.formatFieldLabel(key) || key;

            // Se for um objeto de mudança (de/para) vindo do Agendamento
            if (value && typeof value === 'object' && 'de' in value && 'para' in value) {
              return `${label}: de "${value.de || 'vazio'}" para "${value.para || 'vazio'}"`;
            }

            return `${label}: ${value}`;
          })
          .filter(v => v !== null)
          .join(' | ');
      } catch (e) {
        return data;
      }
    },
    translateTimelineType(tipo) {
      const translations = {
        'telefone_atualizado': 'Telefone Atualizado',
        'telefone_adicionado': 'Novo Telefone',
        'telefone_removido': 'Telefone Removido',
        'email_atualizado': 'E-mail Atualizado',
        'email_adicionado': 'Novo E-mail',
        'email_removido': 'E-mail Removido',
        'endereco_atualizado': 'Endereço Atualizado',
        'endereco_adicionado': 'Novo Endereço',
        'endereco_removido': 'Endereço Removido',
        'telefone_dependente_atualizado': 'Telefone do Dependente Atualizado',
        'telefone_dependente_adicionado': 'Novo Telefone do Dependente',
        'telefone_dependente_removido': 'Telefone do Dependente Removido',
        'email_dependente_atualizado': 'E-mail do Dependente Atualizado',
        'email_dependente_adicionado': 'Novo E-mail do Dependente',
        'email_dependente_removido': 'E-mail do Dependente Removido',
        'endereco_dependente_atualizado': 'Endereço do Dependente Atualizado',
        'endereco_dependente_adicionado': 'Novo Endereço do Dependente',
        'endereco_dependente_removido': 'Endereço do Dependente Removido',
        'segurado_removido': 'Segurado Removido',
        'dependente_inativado': 'Dependente Inativado',
        'cadastro_dependente': 'Dependente Cadastrado',
        'atualizacao_dependente': 'Dependente Atualizado',
        'badge_adicionada': 'Badge Adicionada',
        'badge_removida': 'Badge Removida',
        'atualizacao_agendamento': 'Agendamento Atualizado',
        'visita_agendada': 'Visita Agendada',
        'atualizacao_segurado': 'Cadastro Atualizado',
        'rota_avulsa_criada': 'Nova Rota Avulsa',
        'rota_avulsa_dependente_criada': 'Nova Rota Avulsa (Dependente)'
      };
      return translations[tipo] || tipo.replace(/_/g, ' ').toUpperCase();
    },
    formatFieldLabel(field) {
      const labels = {
        cpf: 'CPF',
        nome: 'Nome',
        data_nascimento: 'Data de Nascimento',
        email: 'Email',
        carreira: 'Carreira',
        telefone: 'Telefone',
        consultor_email: 'Consultor',
        data_retorno: 'Data de Retorno',
        data_visita: 'Data da Visita',
        status: 'Status',
        status_contato: 'Status do Contato',
        endereco: 'Endereço',
        tipo_visita: 'Tipo de Visita',
        telefone_contato: 'Telefone de Contato',
        descricao: 'Descrição',
        observacao: 'Observação',
        numero: 'Número',
        principal: 'Principal',
        logradouro: 'Logradouro',
        bairro: 'Bairro',
        cidade: 'Cidade',
        estado: 'Estado',
        cep: 'CEP',
        complemento: 'Complemento'
      };
      return labels[field] || field;
    },
    validateInput(index) {
      const endereco = this.formData.enderecos[index];
      // Remove números e limita o tamanho a 2 caracteres
      endereco.estado = endereco.estado.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    },
    formatDate(date) {
      if (!date) return 'Não informado';
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      return new Date(date).toLocaleDateString('pt-BR', options);
    },
    async _buscarEmpresas() {
      if (!this.empresaBusca || this.empresaBusca.length < 4) {
        this.sugestoesEmpresas = [];
        return;
      }
      this.getEmpresas(this.empresaBusca);
    },
    async getEmpresas(query) {
      this.loading = true;
			const empresasRepo = new EmpresasRepository();
			try {
				const response = await empresasRepo.getEmpresaQuery(this.onlineUser.token, query);
				if (response?.status === 200) {
          this.loading = false;
          this.sugestoesEmpresas = response.data;
				} else {
          this.loading = false;
					this.error = response?.message;
				}
			} catch (error) {
				this.loading = false;
				handleUnauthorized(error);
			}
		},
    async confirmarVinculo(empresa) {

      const result = await Swal.fire({
        title: 'Deseja vincular esta empresa?',
        text: `Empresa: ${empresa.nome}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, vincular',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      });

      if (result.isConfirmed) {
        this.adicionarEmpresa(empresa.id); // sua função de API aqui
      }
    },
    async adicionarEmpresa(empresa) {
      this.loading = true;
			const seguradoRepo = new SeguradosRepository();
			try {
				
				const response = await seguradoRepo.vincularEmpresas(this.onlineUser.token,empresa,this.segurado.id);
          if (response?.status === 200) {
            this.loading = false;
            this.mostrarAlerta();
            this.$emit('segurado-empresa', this.segurado.id);
          } else {
            this.error = response?.message;
            this.loading = false;
            this.mostrarAlertaErro(this.error);
          }
			} catch (error) {
				this.loading = false;
				handleUnauthorized(error);
				this.mostrarAlertaErro(error);
			}	
    },

    addItem(tipo) {
      const lista = this.formData[tipo];

      const camposVazios = {
        emails: item => !item.email || item.email.trim() === '',
        telefones: item => !item.numero || item.numero.trim() === '',
        enderecos: item => !item.cep || item.cep.trim() === '',
        dependentes: item => !item.cpf || item.cpf.trim() === '',
        conjuges: item => !item.cpf || item.cpf.trim() === '',
      };

      if (!Array.isArray(lista) || !camposVazios[tipo]) return;

      const hasEmpty = lista.some(camposVazios[tipo]);
      if (hasEmpty) {
        this.mostrarAlertaErro(`Preencha ou remova os ${tipo} vazios antes de adicionar um novo.`);
        return;
      }

      const novosModelos = {
        emails: () => ({ email: '', segurado_id: this.formData.id }),
        telefones: () => ({ numero: '', segurado_id: this.formData.id }),
        enderecos: () => ({
          cep: '', logradouro: '', numero: '', bairro: '', cidade: '', estado: '', segurado_id: this.formData.id
        }),
        dependentes: () => ({
          nome: '', cpf: '', email: '', data_nascimento: '', segurado_id: this.formData.id
        }),
        conjuges: () => ({
          nome: '', cpf: '', data_nascimento: '', segurado_id: this.formData.id
        }),
      };

      lista.push(novosModelos[tipo]());
    },

    adicionarMarcacao(badge){
      this.$emit('segurado-badge', badge);
    },

    handleRemove(tipo, index, item) {
      if (item.id) {
        if (tipo === 'dependente') {
          // Deixa o pai decidir se vai tornar inativo ou excluir
          this.$emit('remove-dependente', item);
        } else {
          this.$emit('remove-item',{tipo, item});
        }
      }

      this.formData[tipo + 's'].splice(index, 1);
    },

    removeBadge(id){
      this.$emit('remove-badge', id);
    },

    mostrarAlertaErro(message) {
			Swal.fire({
				icon: 'error',
				title: 'Erro!',
				text: message,
				confirmButtonColor: '#3085d6', 
			});
		},
    mostrarAlerta() {
			Swal.fire({
				icon: 'success',
				title: 'Sucesso!',
				text: 'A operação foi concluída com sucesso.',
				confirmButtonColor: '#3085d6',
			});
		},
  },
  components: {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    CommonLoading,
    BadgesView,
    PlusIcon,
    BadgesForm
  },
};
</script>
  